import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { STORE_API } from "../../store/CartContext";
import { useAccount } from "../../store/AccountContext";
import { StarDisplay, StarInput } from "./Stars";

export const rateProduct = (productId, rating) => axios.put(`${STORE_API}/products/${productId}/review`, { rating }, { withCredentials: true });

export default function ProductRatings({ productId, onChange }) {
  const { user, login } = useAccount();
  const [r, setR] = useState(null);
  const load = useCallback(() => axios.get(`${STORE_API}/products/${productId}/ratings`, { withCredentials: true }).then((x) => { setR(x.data); onChange?.(x.data); }).catch(() => {}), [productId, onChange]);
  useEffect(() => { load(); }, [load, user]);
  if (!r) return null;

  const rate = async (n) => {
    try { await rateProduct(productId, n); toast.success("Thanks for rating!"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save your rating"); }
  };

  return (
    <section data-testid="pdp-ratings" className="container-custom py-6">
      <div className="grid gap-6 rounded-3xl bg-white p-6 md:grid-cols-[220px_1fr_260px]">
        <div>
          <p className="font-heading text-lg font-bold">Customer ratings</p>
          <p data-testid="pdp-rating-average" className="mt-2 font-heading text-5xl font-black">{r.count ? r.average.toFixed(1) : "–"}</p>
          <StarDisplay value={r.average} size="h-4 w-4" />
          <p data-testid="pdp-rating-count" className="mt-1 text-xs text-[#5E5675]">{r.count} verified rating{r.count === 1 ? "" : "s"}</p>
        </div>
        <div className="space-y-1.5 self-center">
          {[5, 4, 3, 2, 1].map((k) => [k, r.distribution[k]]).map(([k, n]) => (
            <div key={k} className="flex items-center gap-2 text-xs font-semibold">
              <span className="w-6">{k}★</span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#3915AA]/10"><div className="h-full rounded-full bg-[#FC5106]" style={{ width: `${r.count ? (n / r.count) * 100 : 0}%` }} /></div>
              <span className="w-6 text-right text-[#5E5675]">{n}</span>
            </div>
          ))}
        </div>
        <div data-testid="pdp-rate-box" className="rounded-2xl bg-[#F7F5FF] p-4">
          {r.can_review ? (
            <>
              <p className="flex items-center gap-1.5 text-sm font-bold"><BadgeCheck className="h-4 w-4 text-green-600" /> Verified buyer</p>
              <p className="mb-2 text-xs text-[#5E5675]">{r.my_rating ? "Your rating (tap to change)" : "How would you rate it?"}</p>
              <StarInput value={r.my_rating} onChange={rate} testid="pdp-rate-input" />
            </>
          ) : user ? (
            <p className="text-xs text-[#5E5675]">Only customers who bought this product can rate it.</p>
          ) : (
            <p className="text-xs text-[#5E5675]">Bought this? <button onClick={() => login(window.location.pathname)} data-testid="pdp-rate-signin" className="font-bold text-[#FC5106]">Sign in</button> to rate it.</p>
          )}
        </div>
      </div>
    </section>
  );
}
